import React from 'react'
import { convertToRaw, RichUtils, EditorState, Modifier } from 'draft-js'
import { connect } from 'react-redux'
import { getTranslation } from '@/store/getters'
import { updateActualElement, updateEditorState } from '@/store/actions'
import { prepareToSaveHTML } from '@/components/commons/editor/configs/ElementsConfig'
import { getAllTextSelect } from '@/components/commons/editor/configs/ElementsConfig'

import styles from './styles.scss'

/** Components */
import FontFamily from '@/components/editors/FontFamily'
import FontSize from '@/components/editors/FontSize'
import FontColor from '@/components/editors/FontColor'
import FontFormat from '@/components/editors/FontFormat'
import FontAlign from '@/components/editors/FontAlign'
import FontLink from '@/components/editors/FontLink'
import Opacity from '@/components/Opacity'
import IconComponent from '../../../../../../components/commons/IconComponent'
import FontHeight from '../../../../../../components/editors/FontHeight/index'

const TextContent = props => {
  const { translation, textEditor, element, user: { accessToken }, updateActualElement } = props
  const { colorsMap, fontSizeMap, fontFamilyMap } = element.content
  const { textAlign, fontFamily, fontSize, opacity, lineHeight } = element.content.styles

  const currentStyles = textEditor.editorState.getCurrentInlineStyle()

  const setStyle = (type, styles) => {
    let { editorState } = textEditor
    let currentSelection = editorState.getSelection()
    const start = currentSelection.getStartOffset()
    const end = currentSelection.getEndOffset()
    let nextEditorState

    //Verify text are selected
    if (start === end) {
      nextEditorState = EditorState.acceptSelection(
        editorState,
        getAllTextSelect(editorState)
      )
      currentSelection = nextEditorState.getSelection()
    }

    const nextContent = Object.keys(styles).reduce((content, style) => {
      return Modifier.removeInlineStyle(content, currentSelection, style)
    }, editorState.getCurrentContent())

    nextEditorState = EditorState.push(
      editorState,
      nextContent,
      'change-inline-style'
    )

    const currentStyle = editorState.getCurrentInlineStyle()

    if (currentSelection.isCollapsed()) {
      nextEditorState = currentStyle.reduce((state, color) => {
        return RichUtils.toggleInlineStyle(state, color)
      }, nextEditorState)
    }

    if (!currentStyle.has(type)) {
      nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, type)
    }

    textEditor.editorState = nextEditorState
    textEditor.updated = Date.now()
    updateEditorState(textEditor)

    if (type.indexOf('fontFamily') !== -1) {
      element.content.styles['fontFamily'] = styles[type].fontFamily
        .replace(/"/g, '')
        .split(',')[0]
      element.content.styles['fontPath'] = styles[type].fontPath
    }

    if (type.indexOf('fontSize') !== -1) {
      element.content.styles['fontSize'] = styles[type].fontSize
    }

    updateElementContent(textEditor)
    save(element)
  }

  const updateElementContent = editor => {
    element.content.html = prepareToSaveHTML(editor.editorState)
    element.content.rawState = convertToRaw(
      editor.editorState.getCurrentContent()
    )
  }

  const getLink = () => {
    const { editorState } = textEditor

    const contentState = editorState.getCurrentContent()
    const selectionState = editorState.getSelection()
    const startKey = selectionState.getStartKey()
    const contentBlock = contentState.getBlockForKey(startKey)
    const startOffset = selectionState.getStartOffset()
    const entity = contentBlock.getEntityAt(startOffset)
    //const endOffset = selectionState.getEndOffset()
    //const selectedText = currentContentBlock.getText().slice(startOffset, endOffset)

    if (!entity) return false

    const linkInstance = contentState.getEntity(entity)
    return linkInstance.getData()
  }

  const save = element => {
    const { id, id_page } = element

    updateActualElement({
      id: id,
      pageID: id_page,
      element,
      accessToken: accessToken
    })
  }

  const handleToggleInline = style => {
    textEditor.editorState = RichUtils.toggleInlineStyle(
      textEditor.editorState,
      style
    )
    updateEditorState(textEditor)

    // save on database
    updateElementContent(textEditor)
    save(element)
  }

  const handleStyle = style => {
    element.content.styles = {
      ...element.content.styles,
      ...style
    }
    save(element)
  }

  const handleFontFamily = (family, font) => {
    let name = family.replace(/\s/g, '-')
    fontFamilyMap[`fontFamily-${name}`] = font.stylesheet
    setStyle(`fontFamily-${name}`, fontFamilyMap)
  }

  const handleFontSize = size => {
    fontSizeMap[`fontSize-${size}`] = {
      fontSize: `${size}px`
    }
    setStyle(`fontSize-${size}`, fontSizeMap)
  }

  const handleFontHeight = height => {
    handleStyle({ lineHeight: `${height}px` })
  }

  const handleColor = color => {
    colorsMap[`color-${color}`] = {
      color
    }
    setStyle(`color-${color}`, colorsMap)
  }

  const handleLink = link => {
    if (link.href) {
      const regex = /https?:\/\//
      const newLink = regex.test(link.url) ? link.url : `http://${link.url}`
      link.url = newLink
    }

    const { editorState } = textEditor
    let currentSelection = editorState.getSelection()
    const startOffset = currentSelection.getStartOffset()
    const endOffset = currentSelection.getEndOffset()
    const contentState = editorState.getCurrentContent()
    const contentStateWithEntity = contentState.createEntity(
      'LINK',
      'MUTABLE',
      link
    )
    let newEditorState = EditorState.push(
      editorState,
      contentStateWithEntity,
      'create-entity'
    )
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey()

    //Verify text are selected
    if (startOffset === endOffset) {
      let nextEditorState = EditorState.acceptSelection(
        editorState,
        getAllTextSelect(editorState)
      )
      currentSelection = nextEditorState.getSelection()
    }

    newEditorState = RichUtils.toggleLink(
      newEditorState,
      currentSelection,
      entityKey
    )

    textEditor.editorState = newEditorState
    updateEditorState(textEditor)
    updateElementContent(textEditor)
    save(element)
  }


  return (
    <div className={styles['text-dashboard']}>

      <hr className={'ba-mg-b-2 ba-hr'} />

      <div className="ba-flex ba-ai-center ba-jc-sa">
        <FontFamily
          label={fontFamily || translation('general-font-family')}
          onChange={(family, font) => handleFontFamily(family, font)}
        />
        <FontFormat currentStyle={currentStyles} onChange={handleToggleInline} />
      </div>

      <hr className={'ba-mg-y-2 ba-hr'} />

      <FontAlign align={textAlign} onChange={handleStyle} />

      <hr className={'ba-mg-y-2 ba-hr'} />

      <div className="ba-flex ba-ai-center ba-jc-sb ba-relative ba-mg-b-1">
        <FontSize
          label={fontSize || translation('general-font-family')}
          onChange={handleFontSize}
        />
        <FontHeight
          label={lineHeight || translation('general-font-height')}
          onChange={handleFontHeight}
        />
        <FontColor onChange={handleColor} />
      </div>

      <hr className={'ba-mg-y-2 ba-hr'} />

      <p className={'ba-font'}>{translation('general-transparency')}</p>
      <Opacity opacity={opacity}
        onChange={opacity => handleStyle({ opacity })} />

      <hr className={'ba-mg-y-2 ba-hr'} />

      <div className="ba-flex ba-ai-center ba-mg-b-1">
        <IconComponent icon="hiperlink" />
        <p className={'ba-font ' + styles['mg-l']}>{translation('hyperlink')}</p>
      </div>

      <FontLink link={getLink()} onChangeLink={handleLink} />

    </div>
  )
}

const mapStateToProps = (state) => {
  const translation = getTranslation(state)
  return {
    translation
  }
}
export default connect(
  mapStateToProps,
  { updateActualElement, updateEditorState }
)(TextContent)



// WEBPACK FOOTER //
// ./src/pages/Editor/components/ContentEditor/editors/TextContent/index.js